import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const lowest = product.dosages.reduce((a, b) => (a.price <= b.price ? a : b));

  const quickAdd = () => {
    addItem(product, lowest);
    toast.success(`${product.name} (${lowest.label}) added to cart`);
  };

  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.1, ease: [0.76, 0, 0.24, 1] }}
      className="group w-full min-w-0 border border-white/10 bg-[#111111] transition-colors hover:border-white/25"
    >
      <Link
        to={`/products/${product.id}`}
        data-testid={`product-view-${product.id}`}
        className="block overflow-hidden"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-black">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover grayscale transition-[transform,filter] duration-700 ease-out group-hover:scale-105 group-hover:grayscale-0"
          />
          <span className="absolute left-3 top-3 bg-black/70 px-2 py-1 font-mono text-[10px] tracking-[0.2em] text-[#00ff72] backdrop-blur-sm">
            {product.category.toUpperCase()}
          </span>
          {product.sold_out && (
            <span className="absolute right-3 top-3 bg-red-500/90 px-2 py-1 font-mono text-[10px] tracking-[0.2em] text-black backdrop-blur-sm">
              SOLD OUT
            </span>
          )}
        </div>
      </Link>
      <div className="space-y-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-xl text-[#F9F6F0]">{product.name}</h3>
        </div>
        <p className="line-clamp-2 text-xs leading-relaxed text-[#A3A3A3]">{product.description}</p>
        <div className="flex items-center justify-between font-mono text-[10px] text-[#666666]">
          <span className="min-w-0 truncate">{product.sequence}</span>
          <span className="ml-2 shrink-0 text-[#A3A3A3]">{product.external_url || product.purity == null ? "" : `${product.purity}%`}</span>
        </div>
        <div className="flex items-center justify-between border-t border-white/10 pt-3">
          {product.external_url ? (
            <>
              <span className="font-mono text-[10px] tracking-[0.2em] text-[#A3A3A3]">PARTNER STORE</span>
              <a
                href={product.external_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`product-shop-${product.id}`}
                className="flex items-center gap-1 border border-[#00ff72] px-3 py-2 font-mono text-[10px] tracking-[0.2em] text-[#00ff72] transition-colors hover:bg-[#00ff72] hover:text-black"
              >
                <ArrowUpRight className="h-3 w-3" /> SHOP
              </a>
            </>
          ) : (
            <>
              <span className="font-mono text-sm text-[#F9F6F0]">
                from <span className="text-[#00ff72]">${lowest.price.toFixed(2)}</span>
              </span>
              {product.sold_out ? (
                <span className="font-mono text-[10px] tracking-[0.2em] text-red-400">SOLD OUT</span>
              ) : (
                <button
                  data-testid={`product-add-${product.id}`}
                  onClick={quickAdd}
                  className="flex items-center gap-1 border border-white/20 px-3 py-2 font-mono text-[10px] tracking-[0.2em] text-[#F9F6F0] transition-colors hover:border-[#00ff72] hover:bg-[#00ff72] hover:text-black"
                >
                  <Plus className="h-3 w-3" /> ADD
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </motion.article>
  );
}
